import React, { useContext } from 'react';
import { QueryKey, useQuery, UseQueryOptions } from 'react-query';

import { getResourceSubmissionFields } from 'services/resources';

import { ResourceSubmissionContext } from '../ResourceSubmissionContext';

export interface IResourceSubmissionFieldsResponse {
  resource_formats: IResourceSubmissionFieldsResourceFormat[];
  resource_submission_categories: IResourceSubmissionFieldsResourceSubmissionCategory[];
  resource_types: IResourceSubmissionFieldsResourceType[];
  max_file_size: number;
  eligible_companies?: IResourceSubmissionFieldsPulseCompany[];
  pulse_companies: IResourceSubmissionFieldsPulseCompany[];
  internal_share_status: string[];
  external_client_share_status: string[];
}

export interface IResourceSubmissionFieldsPulseCompany {
  uuid: string;
  name: string;
  slug: string;
}

export interface IResourceSubmissionFieldsResourceFormat {
  uuid: string;
  name: string;
  prefix?: string;
}

export interface IResourceSubmissionFieldsResourceSubmissionCategory {
  uuid: string;
  name: string;
  slug: string;
  description?: string;
  icon?: string;
  resource_type_slug?: string | null;
  non_competing?: boolean;
}

export interface IResourceSubmissionFieldsResourceType {
  uuid: string;
  name: string;
  slug: string;
  icon: string;
}

export function useResourceSubmissionFields(
  bestOfYear?: number,
  options?: Omit<
    UseQueryOptions<
      IResourceSubmissionFieldsResponse,
      Error,
      IResourceSubmissionFieldsResponse,
      QueryKey
    >,
    'queryKey'
  >
) {
  const { bestOfYear: contextBestOfYear } = useContext(ResourceSubmissionContext);
  const requestedYear = bestOfYear ?? contextBestOfYear;

  return useQuery<IResourceSubmissionFieldsResponse, Error>(
    ['resource_submission_fields', requestedYear],
    async () => {
      const response = await getResourceSubmissionFields(requestedYear);
      if (!response.ok) {
        throw new Error(response.originalError?.message);
      }

      return {
        ...response.data,
        resource_submission_categories: [
          ...(response.data.resource_submission_categories || []),
        ].sort(
          (a, b) =>
            a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }) ||
            a.uuid.localeCompare(b.uuid)
        ),
      };
    },
    {
      ...options,
      cacheTime: 600000,
    }
  );
}
